import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { ConditionOperatorEnum } from '@repo/api-gw-sdk';
import type { Condition } from '@repo/api-gw-sdk';
import { type CSSProperties, type UIEventHandler } from 'react';

import { defaultOperatorFor } from '@/components/conditions/OperatorSelect';
import { HelpIcon } from '@/components/shared/helpIcon';
import {
  conditionValues,
  findValueCondition,
  isValueCondition,
  type FilterOption,
  type FilterProperty,
  type FilterProps,
} from '@/types/advanceFilter';

import { TransactionalTextField } from '../shared/transactionalTextField';
import { Virtuoso } from '../shared/virtuoso';
import { TextDetailsTuple } from '../text/textDetailsTuple';

export type { FilterProps };

export const FreeTextFilter = ({
  placeholder,
  property,
  conditions,
  onChange,
  'data-testid': dataTestId,
  showSearchIcon = true,
  operator = ConditionOperatorEnum.Contains,
}: FilterProps & {
  placeholder: string;
  showSearchIcon?: boolean;
  operator?: ConditionOperatorEnum;
}) => {
  const condition = findValueCondition(conditions, property.name);

  return (
    <FormControl variant='outlined' fullWidth>
      <TransactionalTextField
        showSearchIcon={showSearchIcon}
        showClearIcon
        placeholder={placeholder}
        allowEmptyValue
        data-testid={dataTestId}
        initValue={conditionValues(condition)[0] || ''}
        onChange={(value) => {
          const cleanValue = (value || '').trim();
          onChange(
            conditions
              .filter(
                (x) => isValueCondition(x) && x.property !== property.name
              )
              .concat(
                cleanValue
                  ? [
                      {
                        property: property.name,
                        operator,
                        value: [cleanValue],
                      },
                    ]
                  : []
              )
          );
        }}
      />
    </FormControl>
  );
};

const SELECT_ALL_OPTION_ID = 'select-all';

/** Lists shorter than this render in full; longer ones are virtualized. */
const VIRTUALIZATION_THRESHOLD = 100;

type MultiSelectFilterRowProps = FilterProps & {
  option: FilterOption;
  options: FilterOption[];
  selectedValues: string[];
  dataTestId?: string;
  showNameFirst?: boolean;
  shouldShowId?: boolean;
};

const MultiSelectFilterRow = ({
  option,
  options,
  selectedValues,
  property,
  conditions,
  dataTestId,
  onChange,
  showNameFirst,
  shouldShowId,
}: MultiSelectFilterRowProps) => {
  if (option.id === SELECT_ALL_OPTION_ID) {
    return (
      <SelectAllFilterItem
        options={options}
        selectedResources={selectedValues}
        onChange={onChange}
        conditions={conditions}
        property={property}
      />
    );
  }

  const toggleValue = (isSelected: boolean) => {
    const values = isSelected
      ? [...selectedValues, option.id]
      : selectedValues.filter((x) => x !== option.id);

    const existingCondition = findValueCondition(conditions, property.name);

    onChange(
      conditions
        .filter((x) => x !== existingCondition)
        .concat(
          values.length
            ? [
                {
                  property: property.name,
                  operator:
                    existingCondition?.operator ?? defaultOperatorFor(property),
                  value: values,
                },
              ]
            : []
        )
    );
  };

  return (
    <MultiSelectFilterItem
      option={option}
      dataTestId={dataTestId}
      onChange={toggleValue}
      isChecked={selectedValues.includes(option.id)}
      shouldShowId={!!shouldShowId}
      showNameFirst={!!showNameFirst}
    />
  );
};

export const MultiSelectFilter = ({
  options,
  showSelectAll,
  virtuosoStyles,
  onVirtuosoScroll,
  'data-testid': dataTestId,
  ...props
}: FilterProps & {
  showSelectAll?: boolean;
  options: FilterOption[];
  virtuosoStyles?: CSSProperties;
  onVirtuosoScroll?: UIEventHandler<HTMLDivElement>;
  showNameFirst?: boolean;
  shouldShowId?: boolean;
}) => {
  const selectedValues = conditionValues(
    findValueCondition(props.conditions, props.property.name)
  );

  const rowProps = { ...props, dataTestId, options, selectedValues };

  const allOptions = showSelectAll
    ? [{ id: SELECT_ALL_OPTION_ID }, ...options]
    : options;

  if (allOptions.length < VIRTUALIZATION_THRESHOLD) {
    return (
      <Stack
        sx={{
          gap: '4px',
        }}
      >
        {allOptions.map((option) => (
          <MultiSelectFilterRow key={option.id} {...rowProps} option={option} />
        ))}
      </Stack>
    );
  }

  return (
    <Virtuoso
      style={virtuosoStyles || { height: '100%' }}
      data={allOptions}
      onScroll={onVirtuosoScroll}
      itemContent={(index, option) => (
        <MultiSelectFilterRow key={option.id} {...rowProps} option={option} />
      )}
    />
  );
};

const SelectAllFilterItem = ({
  options,
  selectedResources,
  onChange,
  conditions,
  property,
}: {
  selectedResources: string[];
  onChange: (conditions: Condition[]) => void;
  conditions: Condition[];
  property: FilterProperty;
  options: FilterOption[];
}) => {
  const selectableOptions = options.filter((x) => !x.isGroupTitle);

  return (
    <MultiSelectFilterItem
      shouldShowId={false}
      showNameFirst={false}
      option={{
        id: SELECT_ALL_OPTION_ID,
        name: `Select all (${selectableOptions.length})`,
      }}
      onChange={(newValue: boolean) => {
        const values = newValue ? selectableOptions.map((x) => x.id) : [];
        onChange(
          conditions
            // Unlike a single-value toggle, select-all replaces every condition on
            // this property rather than editing one, so an operator the user can't
            // see here (e.g. an inherited NotIn) can't survive the selection.
            .filter((x) => isValueCondition(x) && x.property !== property.name)
            .concat(
              values.length
                ? [
                    {
                      property: property.name,
                      operator: defaultOperatorFor(property),
                      value: values,
                    },
                  ]
                : []
            )
        );
      }}
      isChecked={selectableOptions.every((x) =>
        selectedResources.includes(x.id)
      )}
    />
  );
};

const MultiSelectFilterItem = ({
  option,
  dataTestId,
  onChange,
  isChecked,
  shouldShowId,
  showNameFirst,
}: {
  option: FilterOption;
  dataTestId?: string;
  isChecked: boolean;
  onChange: (newValue: boolean) => void;
  shouldShowId: boolean;
  showNameFirst: boolean;
}) => {
  if (option.isGroupTitle) {
    return (
      <Typography
        variant='overline'
        sx={{
          paddingY: '8px',
        }}
      >
        {option.name}
      </Typography>
    );
  }

  return (
    <FormControlLabel
      key={option.id}
      className='group/help'
      sx={{
        alignItems: 'center',
        display: 'flex',
        width: '100%',
      }}
      control={
        <Checkbox
          slotProps={{
            input: {
              'data-testid': `${dataTestId ? `${dataTestId}-` : ''}${option.id}`,
            } as Record<string, string>,
          }}
          className='p-[8px] mr-[4px]'
          size='small'
          checked={isChecked}
          onChange={(event, value) => onChange(value)}
        />
      }
      label={
        <Stack
          direction='row'
          className='w-[216px] relative'
          sx={{
            alignItems: 'center',
            gap: '12px',
          }}
        >
          {option.Image}
          {!option.hideName && (
            <TextDetailsTuple
              primaryProp={
                shouldShowId
                  ? showNameFirst
                    ? option.name
                    : option.id
                  : undefined
              }
              secondaryProp={showNameFirst ? option.id : option.name}
            />
          )}
          {option.description && (
            <HelpIcon
              title={option.description}
              className='absolute right-0 cursor-default'
              tooltipProps={{
                slotProps: { tooltip: { sx: { padding: '16px' } } },
              }}
            />
          )}
        </Stack>
      }
    />
  );
};
